import { useMemo } from "react"
import { List, ListItem, ListItemText } from "@mui/material"
import { faBuildings } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { MRT_ColumnDef } from "material-react-table"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { useAppSelector } from "../../../../store/storeHooks"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { selectEffectiveCompanyId } from "../../../../context/contextSelectors"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { BusinessCustomer, CompanyCustomer } from "../../../../customers/customerTypes"
import { FetchCustomersResponse, fetchCustomersForMerchant } from "../../../../customers/customerApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { MERCHANT_ADMIN_ROUTE } from "../../../../../constants/routes"

function transformData(data: FetchCustomersResponse): BusinessCustomer[] {
    return data.customers.flatMap((customer) => {
        if (customer.isOfficeHotel) {
            const userGroups = customer.userGroups ?? customer.customerGroups ?? []

            const tenants: BusinessCustomer[] = userGroups.map((tenant) => ({
                type: "tenant",
                id: tenant.id,
                customerId: customer.id,
                name: tenant.name,
                isActive: tenant.isActive,
                domains: tenant.domains ?? [],
            }))

            return tenants
        } else {
            const company: CompanyCustomer = {
                type: "company",
                id: customer.id,
                name: customer.name,
                isActive: customer.isActive,
                domains: customer.domains ?? [],
            }

            return company
        }
    })
}

type Props = Readonly<{}>

export function CustomerAccessPage({}: Props) {
    const merchantId = useAppSelector(selectEffectiveCompanyId)
    const { l10n } = useAppLocalization()

    const customersResult = useApiQuery(fetchCustomersForMerchant, {
        queryName: "customers-for-merchant",
        dependencies: { merchantId: merchantId ?? 0 },
        select: transformData,
        enabled: Boolean(merchantId),
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(customersResult)

    const columns = useMemo<MRT_ColumnDef<BusinessCustomer>[]>(() => {
        return [
            columnFactory.nameColumn({
                header: l10n.getString("merchant-customer-access-customers-table-name-column"),
                accessorKey: "name",
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("merchant-customer-access-customers-table-domains-column"),
                accessorFn: (originalRow) => originalRow.domains.join(", "),
                Cell: ({ row }) => {
                    return (
                        <List disablePadding>
                            {row.original.domains.map((domain) => (
                                <ListItem key={`domain-${domain}`} disablePadding disableGutters>
                                    <ListItemText primaryTypographyProps={{ variant: "body2" }}>{domain}</ListItemText>
                                </ListItem>
                            ))}
                        </List>
                    )
                },
            }),
            columnFactory.activeInactiveStatusColumn(
                { header: l10n.getString("merchant-customer-access-customers-table-active-column") },

                (row) => row.isActive,
            ),
        ]
    }, [l10n, columnFactory])

    return (
        <>
            <LocalizedStrict id="merchant-customer-access-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MERCHANT_CUSTOMER_ACCESS}
                    title="Customer Access"
                    icon={<FontAwesomeIcon icon={faBuildings} />}
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-shops">
                            <Breadcrumb>Shops</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-customer-access">
                            <Breadcrumb>Customer Access</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <ApiQueryTable columns={columns} />
        </>
    )
}
